/**
 * CustomTable Component
 * ---------------------------------------------------------
 * A highly generic, multi-purpose table built on top of react-table.
 *
 * Supports:
 * - Many table types (User, Organization, Super admin, WhatsApp, Booking, etc.)
 * - Column ordering and persistence (localStorage)
 * - Server-like pagination with react-table
 * - Infinite scroll rendering within a scrollable container
 * - Row selection with type-specific logic
 * - Row-level actions (Edit, Delete, Toggle, Configure, Branch, etc.)
 * - Per-table behaviors like:
 *    - Toggling user status
 *    - Toggling organization status and analytics
 *    - Editing entities (user, booking, header, distributor, coupons, etc.)
 *    - Opening external links (NEWS VIEW)
 *    - WhatsApp configuration
 * - Light/Dark theme-aware styling
 */

import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import commonStyle from "../../Screens/common.module.css";
import { useTable, useSortBy, useFilters, useColumnOrder, usePagination } from "react-table";
import { getFilterObject } from "../../Values/utils";
import { connect, useDispatch } from "react-redux";
import TableHeader from "../TableHeader/TableHeader";
import CustomToggle from "../CustomToggle";
import { updateUserImport, updateUserStatus } from "../../Services/users";
import { updateAPIStatus, updateOrganizationStatus, updateOrganizationAnalyticsData, updateOrganizationData } from "../../Services/organizations";
import ColumnManagerModal from "../Modals/ColumnManagerModal/ColumnManager";
import { setClearFilter, showSnackbarAction } from "../../Redux/actions";
import { useHistory } from "react-router";
import { FiEdit3 } from "react-icons/fi";
import Loading from "../Loading/Loading";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";
import { themeColors } from "../../Components/theme";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';


/**
 * Props definition for CustomTable
 */
type props = {
  tableColumns: any[];
  tableRows: any[] | undefined;

  /** List of selected row indices (react-table page index based) */
  selectedRows: any[];
  setSelectedRows: (data: any[]) => void;

  /** Table type controls behavior for selection, toggles, actions, etc. */
  tableType:
  | "Organization"
  | "Super admin"
  | "WHATSAPP"
  | "Dynamic header"
  | "Booking From Header"
  | "User"
  | "Task"
  | "CallLogs"
  | "API"
  | "Holiday"
  | "NEWS"
  | "LeadDistributor"
  | "LeadRotation"
  | "Coupon"
  | "DrillDown"
  | "Custom Button"
  | "All User List"
  | "NEWS VIEW"
  | "Booking";

  /** Column manager modal visibility */
  showColumnModal: boolean;
  hideColumnModal: () => void;
  selectedRowsData?: any[];

  /** Selected rows' full data objects (optional) */
  setSelectedRowsData?: (data: any[]) => void;

  /** Search input binding (optional) */
  setSearchedItem?: (text: any) => void;

  /** Organization + users info */
  organization: any;
  organizationUsers: any[];
  role: any;

  /** Organization branch/user edit helpers */
  setBranch?: (text: boolean) => void;
  setUid?: (data: string) => void;

  /** Authenticated user object */
  user: any;

  /** API related handlers */
  setApiModal?: (data: boolean) => void;
  setApiEdit?: (data: boolean) => void;
  setApiData?: (data: any) => void;

  /** External pagination / counter handlers (for parent components) */
  setPageNumber?: (data: any) => void;
  setPageCount?: (data: any) => void;

  /** Theme flag */
  theme: any;

  /** Other optional toggles & handlers */
  setToggleCondition?: any;
  handleDeleteOrgUser?: (data: any) => void;
  handleChangePassword?: (data: any) => void;
  handleDeleteLeadDistributor?: (data: any, index: any) => void;
  handleDeleteLeadRotation?: (data: any, index: any) => void;
  handleDeleteCoupon?: (data: any, index: any) => void;
  setHolidayEdit?: (data: boolean) => void;
  setHolidayData?: (data: any) => void;
  setHolidayModal?: (data: boolean) => void;
  handleDeleteHoliday?: (data: any, index: any) => void;
  handleOnCellClickBooking?: (data: any) => void;
  clearFilterTable?: boolean;
  onToggleWhatsApp?: (event: any, row: any) => void;
};


/**
 * CustomTable Implementation
 */
const CustomTable: FunctionComponent<props> = ({
  tableColumns,
  tableRows,
  selectedRows,
  setSelectedRows,
  tableType,
  showColumnModal,
  hideColumnModal,
  selectedRowsData,
  setSelectedRowsData,
  setSearchedItem,
  organization,
  organizationUsers,
  role,
  setBranch,
  setUid,
  user,
  setApiModal,
  setApiEdit,
  setApiData,
  setPageNumber,
  setPageCount,
  theme,
  setToggleCondition,
  handleDeleteOrgUser,
  handleChangePassword,
  handleDeleteLeadDistributor,
  handleDeleteLeadRotation,
  handleDeleteCoupon,
  setHolidayEdit,
  setHolidayData,
  setHolidayModal,
  handleDeleteHoliday,
  handleOnCellClickBooking,
  clearFilterTable,
  onToggleWhatsApp
}) => {
  const dispatcher = useDispatch();
  const history = useHistory();

  /** UI states */
  const [checked, setChecked] = useState(false); // "Select all" checkbox
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [load, setLoad] = useState(false); // Global loading indicator

  /** Distribution model data & index tracking (LeadDistributor) */
  const [distributionData, setDistributionData] = useState();

  const [reassignData, setReassignData] = useState();
  const [indexs, setIndex] = useState(0);

  const [currentLimit, setCurrentLimit] = useState(20);
  const [recordCounts, setRecordsCount] = useState<any[]>([]);
  const [active, setActiveUsers] = useState(0);

  const isMounted = React.useRef(false);

  /**
  * Clear all filters when `clearFilterTable` flag changes
  */
  useEffect(() => {
    if (isMounted.current) {
      clearFilter();
    } else {
      isMounted.current = true;
    }
  }, [clearFilterTable]);

  /**
   * "Select All" across current page
   * Behavior differs by tableType.
   */
  const selectAll = (checked: boolean) => {
    if (
      tableType === "User" ||
      tableType === "Organization" ||
      tableType === "CallLogs" ||
      tableType === "Task" ||
      tableType === "API" ||
      tableType === "NEWS" ||
      tableType === "Booking" ||
      tableType === "Super admin" ||
      tableType === "All User List"
    ) {

      if (checked === true) {
        // Select indices and data from the current react-table page
        if (page) {

          let selectRowsIndex: any[] = [...selectedRows];
          // Push all visible row indices based on page & pageSize
          Array.from(Array(page?.length).keys()).forEach(
            (index) => {
              selectRowsIndex.push(
                pageSize * pageIndex + index + 1
              );
            }
          );
          setSelectedRows(selectRowsIndex);
        }

        // Collect row.original objects as selectedRowsData
        if (page) {
          if (selectedRowsData) {
            let selectRows: any[] = [...selectedRowsData];
            page.forEach((item: any) => {
              selectRows.push(item.original);
            });
            setSelectedRowsData &&
              setSelectedRowsData(selectRows);
          } else {
            let selectRows: any[] = [];
            page.forEach((item: any) => {
              selectRows.push(item.original);
            });
            setSelectedRowsData &&
              setSelectedRowsData(selectRows);
          }
        }
      } else {
        // Unselect all
        setSelectedRows([]);
        setSelectedRowsData && setSelectedRowsData([]);
      }
    } else {
      // For other table types, selection is index-based only
      if (checked === true) {
        setSelectedRows(
          Array.from(Array(page?.length).keys())
        );
      } else {
        setSelectedRows([]);
      }
    }
  };

  /**
 * Memoized columns & data for react-table
 */
  const columns = useMemo(
    () => tableColumns,
    [tableColumns]
  );

  const data = useMemo(
    () => (tableRows ? tableRows : []),
    [tableRows]
  );

  /**
  * Setup react-table with filters, sorting, column order, and pagination.
  */
  const tableInstance: any = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useSortBy,
    useColumnOrder,
    usePagination
  );

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    pageCount,
    setPageSize,
    allColumns,

    //@ts-ignore
    setColumnOrder,
    //@ts-ignore

    toggleHideAllColumns,
  } = tableInstance;

  const { pageIndex, pageSize } = state;

  /**
 * On mount:
 *  - Set default pageSize to 100
 *  - Restore column order from localStorage if found
 */
  useEffect(() => {
    setPageSize(100);
    const savedColumns = localStorage.getItem("columns");
    if (savedColumns) {
      setColumnOrder(JSON.parse(savedColumns));
    }
    // eslint-disable-next-line
  }, []);

  /**
 * Compute active users count whenever organizationUsers changes.
 */
  useEffect(() => {
    if (organizationUsers) {
      let activeUsers = organizationUsers.filter(
        (item) => item.status === "ACTIVE"
      );
      setActiveUsers(activeUsers.length);
    }
  }, [organizationUsers]);

  /**
 * Build record count dropdown values: fixed list + total rows count
 */
  useEffect(() => {
    let l = [10, 25, 50, 100, 200, 500];
    l.push(rows.length);
    setRecordsCount(l);
  }, [rows]);

  /**
 * Update column order + persist in localStorage
 */
  const setColumns = (columnList: any) => {
    setColumnOrder(columnList);
    localStorage.setItem(
      "columns",
      JSON.stringify(columnList)
    );
  };

  /**
 * Builds filter object for Redux / global filter usage
 */
  useEffect(() => {
    if (tableRows) {
      getFilterObject(tableRows, dispatcher);
    }
  }, [tableRows, dispatcher]);

  /**
 * Checks whether any user is reporting to the given email.
 * Used before inactivating a user.
 */
  const checkStatus = (email: string) => {
    const l = data.filter(
      (item) => item.reporting_to === email
    );
    if (l.length > 0) {
      return false;
    } else {
      return true;
    }
  };

  /**
 * Toggle status for:
 * - Organization users
 * - Super admin organizations
 * - API keys
 */
  const onToggle = (event: any, row: any) => {
    if (tableType === "Organization") {
      setLoad(true);
      if (!event.target.checked) {
        // Turning OFF (INACTIVE)
        let check = checkStatus(row.original.user_email);
        if (check === true) {
          updateUserStatus(
            row.original.uid,
            "INACTIVE",
            (data) => setLoad(data),
            dispatcher
          );
        } else {
          dispatcher(
            showSnackbarAction(
              "Some Users Are Reporting To This User!!",
              "warning"
            )
          );
          setLoad(false);
        }
      } else {
        // Turning ON (ACTIVE)
        let activeUsers = organizationUsers.filter(
          (item) => item.status === "ACTIVE"
        );

        // Trial period active user limit check
        if (organization.trial_period === true) {
          if (
            activeUsers.length ===
            Number(organization.org_trial_period_users_licenses)
          ) {
            dispatcher(
              showSnackbarAction(
                "Maximum active users limit reached in Trail Period, Please Renew to activate more users!!",
                "warning"
              )
            );
            setLoad(false);
          }
          else {
            updateUserStatus(
              row.original.uid,
              "ACTIVE",
              (data) => setLoad(data),
              dispatcher
            );
          }
        }
        // Normal subscription limit check
        else {
          if (
            activeUsers.length ===
            Number(organization.no_of_employees)
          ) {
            dispatcher(
              showSnackbarAction(
                "Maximum active users limit reached!!",
                "warning"
              )
            );
            setLoad(false);
          }
          else {
            updateUserStatus(
              row.original.uid,
              "ACTIVE",
              (data) => setLoad(data),
              dispatcher
            );
          }
        }
      }
    }
    if (tableType === "Super admin") {
      setLoad(true);
      if (!event.target.checked) {
        updateOrganizationStatus(
          row.original.organization_id,
          "INACTIVE",
          (data) => setLoad(data)
        );
      } else {
        updateOrganizationStatus(
          row.original.organization_id,
          "ACTIVE",
          (data) => setLoad(data)
        );
      }
    }
    if (tableType === "API") {
      setLoad(true);
      if (!event.target.checked) {
        updateAPIStatus(
          row.original.api_key,
          "INACTIVE",
          (data) => setLoad(data)
        );
      } else {
        updateAPIStatus(
          row.original.api_key,
          "ACTIVE",
          (data) => setLoad(data)
        );
      }
    }
  };

  /**
 * Handle viewing of NEWS row (NEWS VIEW tableType)
 */
  const handleViewNews = (data: any) => {
    if (data?.link) {
      window.open(data.link, '_blank');
    } else {
      console.log("No link found in data");
    }
  }

  /**
 * Navigation for WhatsApp configuration rows
 */
  const handleWhatsapp = (data: any) => {
    if (data.type === "Simply WhatsApp") {
      history.push("/simplyWhatsAppIntegration");
    }
    else if (data.type === "WAPI") {
      history.push("/wapiWhatsAppIntegration");
    }
    else if (data.type === "ChatSimplified") {
      history.push("/chatSimplifiedIntegration");
    }
  }

  /**
 * Toggle user status for "All User List" tableType
 */
  const onToggleUserList = (event: any, row: any) => {
    if (tableType === "All User List") {
      setLoad(true);
      if (!event.target.checked) {
        let check = checkStatus(row.original.user_email);
        if (check === true) {
          updateUserStatus(
            row.original.uid,
            "INACTIVE",
            (data) => setLoad(data),
            dispatcher
          );
        } else {
          dispatcher(
            showSnackbarAction(
              "Some Users Are Reporting To This User!!",
              "warning"
            )
          );
          setLoad(false);
        }
      } else {
        let activeUsers = organizationUsers.filter(
          (item) => item.status === "ACTIVE"
        );

        if (organization.trial_period === true) {
          if (
            activeUsers.length ===
            Number(organization.org_trial_period_users_licenses)
          ) {
            dispatcher(
              showSnackbarAction(
                "Maximum active users limit reached in Trail Period, Please Renew to activate more users!!",
                "warning"
              )
            );
            setLoad(false);
          }
          else {
            updateUserStatus(
              row.original.uid,
              "ACTIVE",
              (data) => setLoad(data),
              dispatcher
            );
          }
        }
        else {
          if (
            activeUsers.length ===
            Number(organization.no_of_employees)
          ) {
            dispatcher(
              showSnackbarAction(
                "Maximum active users limit reached!!",
                "warning"
              )
            );
            setLoad(false);
          }
          else {
            updateUserStatus(
              row.original.uid,
              "ACTIVE",
              (data) => setLoad(data),
              dispatcher
            );
          }
        }
      }
    }
  };

  /**
  * Toggle "showAnalytics" flag on organizations
  */
  const onToggleAnalytics = (event: any, row: any) => {
    setLoad(true);
    if (!event.target.checked) {
      updateOrganizationAnalyticsData(
        row.original.organization_id,
        { showAnalytics: false },
        (data) => setLoad(data)
      );
    } else {
      updateOrganizationAnalyticsData(
        row.original.organization_id,
        { showAnalytics: true },
        (data) => setLoad(data)
      );
    }
  };

  /**
 * Toggle "allowDuplicateLeads" flag on organizations
 */
  const onToggleShowData = (event: any, row: any) => {
    setLoad(true);
    if (!event.target.checked) {
      updateOrganizationData(
        row.original.organization_id,
        { allowDuplicateLeads: false },
        (data) => setLoad(data)
      );
    } else {
      updateOrganizationData(
        row.original.organization_id,
        { allowDuplicateLeads: true },
        (data) => setLoad(data)
      );
    }
  };

  /**
    * Row checkbox handler.
    * Uses index-based selection (pageIndex/pageSize) for many table types.
    */
  const onCheck = (event: any, row: any, index: number) => {
    if (
      tableType === "User" ||
      tableType === "Organization" ||
      tableType === "Task" ||
      tableType === "API" ||
      tableType === "Booking" ||
      tableType === "NEWS" ||
      tableType === "Super admin" ||
      tableType === "All User List"
    ) {
      if (event.target.checked) {
        let data = [...selectedRows];

        data.push(index);
        setSelectedRows(data);
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          rowData.push(row);
          setSelectedRowsData &&
            setSelectedRowsData(rowData);
        }
      } else if (!event.target.checked) {
        let data = [...selectedRows];
        let item = selectedRows.indexOf(index);
        if (item > -1) {
          data.splice(item, 1);
        }
        setSelectedRows(data);
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          let val = selectedRowsData.indexOf(row);
          if (val > -1) {
            rowData.splice(val, 1);
          }
          setSelectedRowsData &&
            setSelectedRowsData(rowData);
        }
      }
    }
    // Additional generic selection logic (index-based)
    if (event.target.checked) {
      let data = [...selectedRows];
      data.push(index);
      setSelectedRows(data);
    } else if (!event.target.checked) {
      let data = [...selectedRows];
      let item = selectedRows.indexOf(index);
      if (item > -1) {
        data.splice(item, 1);
      }
      setSelectedRows(data);
    }
  };

  /**
 * Clear:
 * - selected state
 * - search string
 * - localStorage (except config keys)
 * - filter Redux flag
 * - optionally redirect (for "User" tableType)
 */
  const clearFilter = () => {
    setChecked(false);
    selectAll(false);
    setSearchedItem && setSearchedItem("");
    var key: any;
    for (var i = 0; i < localStorage.length; i++) {
      key = localStorage.key(i);

      if (key !== "columns" && key !== 'isLightMode' && key !== 'totalMonths' && key !== 'lastAlertTime') {
        localStorage.removeItem(key);
      }
    }
    dispatcher(setClearFilter(true));
    if (tableType === "User") {
      history.push("/");
    }
  };

  /**
 * Theme based styles (background, text, borders, buttons)
 */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const footerStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const buttonStyles = {
    backgroundColor: theme ? themeColors.buttonPrimary : themeColors.buttonPrimaryDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const trBorderStyles = {
    borderBottom: theme ? `1px solid ${themeColors.trBorderColor}` : `1px solid ${themeColors.trBorderColorDark}`,
  }

  /**
    * ----------------------
    *        RENDER
    * ----------------------
    */
  return (
    <>
      {/* Main scrollable container */}
      <div className={commonStyle.child} id="scrollable">

        {/* Global loading overlay */}
        {load === true && <Loading />}
        <div style={{ width: "max-content" }}>

          {/* Table header (checkbox + column headers) */}
          <TableHeader
            headerGroups={headerGroups}
            selectAll={(data) => {
              selectAll(data);
              setChecked(data);
            }}
            panel={tableType}
            allCheck={checked}
          />

          {/* Infinite scroll wrapper */}
          <InfiniteScroll
            style={{ overflow: "visible", minHeight: "100vh", width: "100%", marginBottom: "-10rem", ...tdStyles }}
            dataLength={currentLimit}
            next={() => setCurrentLimit(currentLimit + 20)}
            hasMore={rows.length > currentLimit}
            loader={""}
            scrollableTarget="scrollable"
            className={commonStyle.infiniteScroll}
          >
            {tableRows && (
              <div {...getTableBodyProps()}>
                {page
                  .slice(0, currentLimit)
                  .map((row: any, index: number) => {
                    prepareRow(row);
                    return (
                      <div
                        {...row.getRowProps()}
                        style={
                          row.original.status === "INACTIVE"
                            ? {
                              display: "flex",
                              flexDirection: "row",
                              ...trBorderStyles
                            }
                            : {
                              display: "flex",
                              flexDirection: "row",
                              ...trBorderStyles
                            }
                        }
                      >
                        {/* Sticky first column: checkbox + edit + row index */}
                        <div className={commonStyle.sticky} style={tdStyles}>
                          {tableType === "NEWS VIEW" || tableType === "Booking" || tableType === "WHATSAPP" ?
                            <>

                              {/* For these table types, no checkbox is rendered here */}

                            </> : <>

                              {/* Row Checkbox */}
                              <input
                                style={{ marginRight: "10px", accentColor: "#272944" }}
                                type="checkbox"
                                disabled={row.original?.createNewsProfile === "Super Admin" && role === "organization" ? true : false}
                                onChange={(e) => {
                                  if (
                                    row.original
                                      .transfer_status === true
                                  ) {
                                    dispatcher(
                                      showSnackbarAction(
                                        "This Lead Cannot be Transfered",
                                        "error"
                                      )
                                    );
                                  } else {
                                    onCheck(
                                      e,
                                      row.original,
                                      pageSize * pageIndex +
                                      index +
                                      1
                                    );
                                  }
                                }}
                                checked={selectedRows.includes(
                                  pageSize * pageIndex +
                                  index +
                                  1
                                )}
                              />
                            </>}

                          {/* Edit icon (for most non-Task, non-NEWS/WHATSAPP tables) */}
                          {(
                            tableType !== "Task" &&
                            tableType !== "NEWS VIEW" &&
                            tableType !== "NEWS" &&
                            tableType !== "WHATSAPP" &&
                            user.user_first_name !== "Team" &&
                            (
                              tableType !== "Booking" ||
                              (tableType === "Booking" && user?.profile === "Operation Manager")
                            )
                          ) && (
                              <FiEdit3
                                size={15}
                                color="#272944"
                                style={{ marginRight: '7px' }}
                                onClick={() => {
                                  // Routing behavior based on tableType
                                  if (
                                    tableType ===
                                    "Organization" || tableType === "All User List"
                                  ) {
                                    history.push({
                                      pathname: "/addUsers",
                                      state: {
                                        detail: row.original,
                                        edit: true,
                                      },
                                    });
                                  }
                                  else if (
                                    tableType === "User"
                                  ) {
                                    if (
                                      row.original
                                        .transfer_status ===
                                      true
                                    ) {
                                      dispatcher(
                                        showSnackbarAction(
                                          "This Lead Cannot be Edited",
                                          "error"
                                        )
                                      );
                                    } else {
                                      history.push({
                                        pathname:
                                          "/editContacts",
                                        state: {
                                          detail:
                                            row.original,
                                        },
                                      });
                                    }
                                  }
                                  else if (
                                    tableType ===
                                    "Super admin"
                                  ) {
                                    history.push({
                                      pathname:
                                        "/editOrganizations",
                                      state: {
                                        detail: row.original,
                                      },
                                    });
                                  }
                                  else if (
                                    tableType ===
                                    "Booking From Header"
                                  ) {
                                    history.push({
                                      pathname:
                                        "/editForm",
                                      state: {
                                        detail: row.original,
                                      },
                                    });
                                  }
                                  else if (
                                    tableType ===
                                    "Dynamic header"
                                  ) {
                                    history.push({
                                      pathname:
                                        "/editHeaders",
                                      state: {
                                        detail: row.original,
                                      },
                                    });
                                  } else if (
                                    tableType ===
                                    "LeadDistributor"
                                  ) {
                                    setShowDistributionModal(
                                      true
                                    );
                                    setDistributionData(
                                      row.original
                                    );
                                    setIndex(index);
                                  }
                                  else if (
                                    tableType ===
                                    "LeadRotation"
                                  ) {
                                    setShowReassignModal(
                                      true
                                    );
                                    setReassignData(
                                      row.original
                                    );
                                    setIndex(index);
                                  }
                                  else if (
                                    tableType === "API"
                                  ) {
                                    setApiEdit &&
                                      setApiEdit(true);
                                    setApiModal &&
                                      setApiModal(true);
                                    setApiData &&
                                      setApiData(
                                        row.original
                                      );
                                  }
                                  else if (
                                    tableType === "Booking"
                                  ) {
                                    history.push({
                                      pathname: '/editBooking',
                                      state: { "paramsData": row.original }
                                    })
                                  }
                                  else if (tableType === "Holiday") {
                                    setHolidayEdit &&
                                      setHolidayEdit(true);
                                    setHolidayModal &&
                                      setHolidayModal(true);
                                    setHolidayData &&
                                      setHolidayData(
                                        row.original
                                      );
                                  }
                                }}
                              />
                            )}

                          {/* Lead Distributor edit route when modal state is true */}
                          {showDistributionModal && (
                            history.push({
                              pathname: "/editDistributorLogic",
                              state: {
                                data: distributionData, // Passing the data
                                index: indexs, // Passing the index
                              },
                            })
                          )}

                          {/* Lead Reassign edit route when modal state is true */}
                          {showReassignModal && (
                            history.push({
                              pathname: "/editReassignLogic",
                              state: {
                                data: reassignData, // Passing the data
                                index: indexs, // Passing the index
                              },
                            })
                          )}

                          {/* Row index display */}
                          {pageSize * pageIndex + index + 1}
                        </div>

                        {/*  render table data all at a time */}
                        {row.cells.map((cell: any) => {
                          return (
                            <div
                              className={commonStyle.td}
                              {...cell.getCellProps()}
                              style={tableType === "LeadDistributor" || tableType === "LeadRotation" || tableType === "Coupon" ? { minWidth: "210px", maxWidth: "210px", ...tdStyles } : { ...tdStyles }}
                              onClick={() => {
                                if (
                                  cell.column.Header ===
                                  "Customer Name" &&
                                  tableType === "User"
                                ) {
                                  history.push({
                                    pathname:
                                      "/contactDetails",
                                    state: {
                                      detail: {
                                        ...cell.row
                                          .original,
                                        created_at:
                                          cell.row.original[
                                            "created_at"
                                          ].toDate(),
                                        lead_assign_time:
                                          cell.row.original[
                                            "lead_assign_time"
                                          ].toDate(),
                                        next_follow_up_date_time:
                                          cell.row.original[
                                            "next_follow_up_date_time"
                                          ]
                                            ? cell.row.original[
                                              "next_follow_up_date_time"
                                            ].toDate()
                                            : "",
                                        modified_at: cell
                                          .row.original[
                                          "modified_at"
                                        ]
                                          ? cell.row.original[
                                            "modified_at"
                                          ].toDate()
                                          : "",
                                        stage_change_at:
                                          cell.row.original[
                                            "stage_change_at"
                                          ]
                                            ? cell.row.original[
                                              "stage_change_at"
                                            ].toDate()
                                            : "",
                                      },
                                    },
                                  });
                                }
                              }}
                            >
                              {cell.render("Cell")}
                            </div>
                          );
                        })}

                        {/* Super admin - allowDuplicateLeads toggle */}
                        {tableType === "Super admin" && (
                          <div
                            className={commonStyle.td}
                            style={{ ...tdStyles, marginLeft: "2%" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent:
                                  "space-between",
                              }}
                            >
                              <CustomToggle
                                size="small"
                                onChange={(e) => {
                                  onToggle(e, row)
                                }}
                                checked={
                                  row.original.status ===
                                    "ACTIVE"
                                    ? true
                                    : false
                                }
                              />
                            </div>
                          </div>
                        )}

                        {/* Super admin - showAnalytics toggle */}
                        {tableType === "Super admin" && (
                          <div
                            className={commonStyle.td}
                            style={{ ...tdStyles, marginLeft: "2%" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent: "space-between",
                              }}
                            >
                              <CustomToggle
                                size="small"
                                onChange={(e) => {
                                  onToggleShowData(e, row)
                                }}
                                checked={
                                  row.original?.allowDuplicateLeads ===
                                    true
                                    ? true
                                    : false
                                }
                              />
                            </div>
                          </div>
                        )}


                        {tableType === "Super admin" && (
                          <div
                            className={commonStyle.td}
                            style={{ ...tdStyles, marginLeft: "2%" }}

                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent:
                                  "space-between",
                              }}
                            >
                              <CustomToggle
                                size="small"
                                onChange={(e) => {
                                  onToggleAnalytics(e, row)
                                }
                                }
                                checked={
                                  row.original.showAnalytics ===
                                    true
                                    ? true
                                    : false
                                }
                              />
                            </div>
                          </div>
                        )}
                        {tableType === "Super admin" ? (
                          <div
                            style={{ paddingLeft: "30px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              onClick={() => {
                                handleChangePassword && handleChangePassword(row.original);
                              }}
                            >
                              Change Password
                            </button>
                          </div>
                        ) : null}

                        {/* Booking - View Details (non Operation Manager users) */}
                        {tableType === "Booking" && user?.profile != "Operation Manager" ? (
                          <div
                            style={{ paddingLeft: "52px", marginTop: "5px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              onClick={() => {
                                handleOnCellClickBooking && handleOnCellClickBooking(row.original);
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        ) : null}

                        {/* Super admin - Delete Organization */}
                        {tableType === "Super admin" ? (
                          <div
                            style={{ paddingLeft: "70px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteOrgUser && handleDeleteOrgUser(row.original)}
                            >
                              Delete Organization
                            </button>
                          </div>
                        ) : null}

                        {tableType === "CallLogs" && (
                          <div
                            className={commonStyle.td}
                            style={tdStyles}
                          >
                            <p></p>
                          </div>
                        )}

                        {tableType === "All User List" && (
                          <div
                            className={commonStyle.td}
                            style={tdStyles}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent:
                                  "space-between",
                              }}
                            >
                              {(tableType === "All User List") && (
                                <CustomToggle
                                  size="small"
                                  onChange={(e) =>
                                    onToggleUserList(e, row)
                                  }
                                  checked={
                                    row.original.status ===
                                      "ACTIVE"
                                      ? true
                                      : false
                                  }
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* WhatsApp integration toggles */}
                        {tableType === "WHATSAPP" && (
                          <div
                            className={commonStyle.td}
                            style={{ ...tdStyles, marginLeft: "4%" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent:
                                  "space-between",
                              }}
                            >
                              {(tableType === "WHATSAPP") && (
                                <CustomToggle
                                  size="small"
                                  onChange={(e) => onToggleWhatsApp?.(e, row)}
                                  checked={row.original.active}
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* WhatsApp - Configure button */}
                        {tableType === "WHATSAPP" ? (
                          <div
                            style={{ paddingLeft: "50px", marginTop: "6px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              onClick={() => {
                                handleWhatsapp && handleWhatsapp(row.original);
                              }}
                            >
                              Configure
                            </button>
                          </div>
                        ) : null}

                        {/* NEWS VIEW - View News button */}
                        {tableType === "NEWS VIEW" ? (
                          <div
                            style={{ paddingLeft: "50px", marginTop: "6px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              onClick={() => {
                                handleViewNews && handleViewNews(row.original);
                              }}
                            >
                              View News
                            </button>
                          </div>
                        ) : null}

                        {/* All User List - Change Password button */}
                        {tableType === "All User List" ? (
                          <div
                            style={{ paddingLeft: "95px", marginLeft: "10px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              onClick={() => {
                                handleChangePassword && handleChangePassword(row.original);
                              }}
                            >
                              Change Password
                            </button>
                          </div>
                        ) : null}

                        {/* All User List - Delete User button */}
                        {tableType === "All User List" ? (
                          <div
                            style={{ paddingLeft: "70px", ...tdStyles }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteOrgUser && handleDeleteOrgUser(row.original)}
                            >
                              Delete User
                            </button>
                          </div>
                        ) : null}

                        {/* Organization - Status toggle */}
                        {tableType === "Organization" && (
                          <div
                            className={commonStyle.td}
                            style={tdStyles}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                                justifyContent:
                                  "space-between",
                              }}
                            >
                              {(tableType ===
                                "Organization" ||
                                tableType ===
                                "Super admin" ||
                                tableType === "API") && (
                                  <CustomToggle
                                    size="small"
                                    onChange={(e) =>
                                      onToggle(e, row)
                                    }
                                    checked={
                                      row.original.status ===
                                        "ACTIVE"
                                        ? true
                                        : false
                                    }
                                  />
                                )}
                            </div>
                          </div>
                        )}

                        {/* Organization - Status toggle */}
                        {tableType === "Organization" ? (
                          <div
                            style={{ paddingLeft: "95px", marginLeft: "10px", ...tdStyles }}
                          >
                            {row.original.profile ===
                              "Lead Manager" ? (
                              <button
                                className={
                                  commonStyle.branchButton
                                }
                                onClick={() => {
                                  setBranch &&
                                    setBranch(true);
                                  setUid &&
                                    setUid(
                                      row.original.uid
                                    );
                                }}
                              >
                                Branch
                              </button>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Holiday - Delete */}
                        {tableType === "Holiday" ? (
                          <div
                            style={{ paddingLeft: "50px", ...tdStyles, marginTop: "5px" }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteHoliday && handleDeleteHoliday(row.original, index)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}

                        {/* LeadDistributor - Delete */}
                        {tableType === "LeadDistributor" ? (
                          <div
                            style={{ paddingLeft: "50px", ...tdStyles, marginTop: "5px" }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteLeadDistributor && handleDeleteLeadDistributor(row.original, index)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}

                        {/* LeadRotation - Delete */}
                        {tableType === "LeadRotation" ? (
                          <div
                            style={{ paddingLeft: "50px", ...tdStyles, marginTop: "5px" }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteLeadRotation && handleDeleteLeadRotation(row.original, index)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}

                        {/* Coupon - Delete */}
                        {tableType === "Coupon" ? (
                          <div
                            style={{ paddingLeft: "50px", ...tdStyles, marginTop: "5px" }}
                          >
                            <button
                              className={
                                commonStyle.branchButton
                              }
                              style={{ backgroundColor: "red" }}
                              onClick={() => handleDeleteCoupon && handleDeleteCoupon(row.original, index)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>

      {/* FOOTER: pagination controls and counts */}
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: "center", borderRadius: "0.5rem", padding: "0.5rem", marginTop: "1rem", marginBottom: "-1rem", ...footerStyles }}>
        <div className={commonStyle.footer_table} style={{ height: "35px" }}>
          {/* Selected records */}
          <p className={commonStyle.countHead} style={{ fontSize: "12px", paddingTop: "7px" }} >
            Selected Records: {selectedRows.length}
          </p>

          {/* Pagination controls */}
          <div style={{
            display: "flex",
            alignItems: "center",
            margin: "0 0 0 0"
          }}>

            {/* Go to page input */}
            <span>
              Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
            </span>
            <span>
              | Go to Page:{" "}
              <input
                type="number"
                className={commonStyle.nSelecct}
                min={1}
                max={pageOptions.length}
                defaultValue={pageIndex + 1}
                onChange={(e) => {
                  let pageNumber = e.target.value ? Number(e.target.value) : 1;
                  if (pageNumber < 1) pageNumber = 1;
                  if (pageNumber > pageOptions.length) pageNumber = pageOptions.length;
                  gotoPage(pageNumber - 1);
                  setPageNumber && setPageNumber(pageNumber);
                }}
                style={{
                  width: "55px",
                  height: "24px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#222",
                  fontSize: "12px",
                  textAlign: "center",
                  margin: "0 4px"
                }}
              />
            </span>

            {/* Page size dropdown */}
            <select
              value={pageSize}
              className={commonStyle.dropDownSelecct}
              style={{
                fontSize: "12px",
                fontWeight: 400,
                height: "24px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                background: "#fff",
                color: "#222",
                margin: "0 4px"
              }}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageCount && setPageCount(Number(e.target.value));
              }}
            >
              {recordCounts.map((pagesize) => (
                <option key={pagesize} value={pagesize}>
                  Show {pagesize}
                </option>
              ))}
            </select>

            {/* First page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                gotoPage(0);
                setChecked(false);
                setPageNumber && setPageNumber(1);
              }}
              disabled={!canPreviousPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canPreviousPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canPreviousPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              {"<<"}
            </button>

            {/* Previous page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                previousPage();
                setChecked(false);
                setPageNumber && setPageNumber(pageIndex);
              }}
              disabled={!canPreviousPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canPreviousPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canPreviousPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronLeftIcon style={{ fontSize: "16px" }} />
            </button>

            {/* Next page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                nextPage();
                setChecked(false);
                setPageNumber && setPageNumber(pageIndex + 2);
              }}
              disabled={!canNextPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canNextPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canNextPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronRightIcon style={{ fontSize: "16px" }} />
            </button>

            {/* Last page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                gotoPage(pageCount - 1);
                setChecked(false);
                setPageNumber && setPageNumber(pageCount);
              }}
              disabled={!canNextPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canNextPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canNextPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              {">>"}
            </button>
          </div>
          <p style={{ fontSize: "12px", paddingTop: "7px" }} className={commonStyle.countHead}>
            Total Records: {rows.length ? rows.length : 0}
          </p>
        </div>
      </div>
      {showColumnModal === true && (
        <ColumnManagerModal
          columnsList={allColumns}
          open={showColumnModal}
          close={hideColumnModal}
          onChangeColumns={(data: any[]) =>
            setColumns(data)
          }
          toggleAllColumns={toggleHideAllColumns}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    role: state.user.role,
    organization: state.organization,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(CustomTable);